/**
 * React Query hooks for sandbox operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useSandboxes
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { sandboxService } from '@/services';
import { showErrorToast } from '@/utils/errorToast';
import { sandboxGroupKeys } from './useSandboxGroups';
import type {
  SandboxRequest,
  SandboxResponse,
  SnapshotRequest,
  CommitSandboxRequest,
  AddPortRequest,
  PatchPortRequest,
  RemovePortRequest,
  UpdatePortsRequest,
  EgressPolicy,
  SandboxLifecyclePolicy,
  SandboxVolumeMount,
  AddPodVolumeMountsRequest,
} from '@/types';

/**
 * Query key factory for sandboxes.
 * Ensures consistent cache key generation.
 */
export const sandboxKeys = {
  all: ['sandboxes'] as const,
  lists: () => [...sandboxKeys.all, 'list'] as const,
  list: () => [...sandboxKeys.lists()] as const,
  details: () => [...sandboxKeys.all, 'detail'] as const,
  detail: (id: string) => [...sandboxKeys.details(), id] as const,
};

/**
 * Hook to fetch total count of sandboxes
 *
 * @example
 * const { data: count } = useSandboxesCount();
 */
export function useSandboxesCount() {
  return useQuery({
    queryKey: [...sandboxKeys.all, 'count'],
    queryFn: () => sandboxService.getCount(),
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}

/**
 * Hook to fetch sandboxes with pagination
 *
 * @param page - Page number (1-based)
 * @param pageSize - Number of items per page
 * @example
 * const { data, isLoading, error } = useSandboxes(1, 25);
 */
export function useSandboxes(page: number = 1, pageSize: number = 25) {
  console.log('[useSandboxes] Hook called - Page:', page, 'PageSize:', pageSize);
  return useQuery({
    queryKey: [...sandboxKeys.list(), page, pageSize],
    queryFn: async () => {
      console.log('[useSandboxes] queryFn executing...');
      const result = await sandboxService.list(page, pageSize);
      console.log('[useSandboxes] queryFn completed, result:', result);
      return result;
    },
  });
}

/**
 * Hook to fetch a single sandbox by ID
 *
 * @param id - The sandbox ID
 * @example
 * const { data, isLoading, error } = useSandbox('sandbox-123');
 */
export function useSandbox(id: string, sandboxGroupId?: string) {
  return useQuery({
    queryKey: sandboxKeys.detail(id),
    queryFn: () => sandboxService.getById(id, sandboxGroupId),
    enabled: !!id,
  });
}

/**
 * Hook to fetch egress decisions for a sandbox
 *
 * @param id - The sandbox ID
 * @param enabled - Whether to enable the query (defaults to true). Set to false when sandbox is not running.
 * @example
 * const { data, isLoading, error } = useEgressDecisions('sandbox-123', isRunning);
 */
export function useEgressDecisions(id: string, enabled: boolean = true, sandboxGroupId?: string) {
  return useQuery({
    queryKey: [...sandboxKeys.detail(id), 'egress-decisions'],
    queryFn: () => sandboxService.getEgressDecisions(id, sandboxGroupId),
    enabled: !!id && enabled,
    staleTime: 5 * 1000, // Cache for 5 seconds
    refetchInterval: enabled ? 5 * 1000 : false, // Only auto-refetch when enabled
  });
}

/**
 * Hook to set the egress policy for a sandbox
 */
export function useSetEgressPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      policy,
      sandboxGroupId,
    }: {
      sandboxId: string;
      policy: EgressPolicy;
      sandboxGroupId?: string;
    }) => sandboxService.setEgressPolicy(sandboxId, policy, sandboxGroupId),
    onSuccess: (_, { sandboxId }) => {
      // Invalidate the sandbox detail to refetch with updated policy
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      toast.success('Egress policy updated successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update egress policy: ${error.message}`);
    },
  });
}

/**
 * Hook to set the lifecycle policy for a sandbox
 */
export function useSetLifecyclePolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      policy,
      sandboxGroupId,
    }: {
      sandboxId: string;
      policy: SandboxLifecyclePolicy;
      sandboxGroupId?: string;
    }) => sandboxService.setLifecyclePolicy(sandboxId, policy, sandboxGroupId),
    onSuccess: (_, { sandboxId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      toast.success('Lifecycle policy updated successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update lifecycle policy: ${error.message}`);
    },
  });
}

/**
 * Hook to create a new sandbox
 *
 * @example
 * const { mutate, isPending } = useCreateSandbox();
 * mutate(sandboxData, { onSuccess: () => navigate('/sandboxes') });
 */
export function useCreateSandbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SandboxRequest) => sandboxService.create(request),
    onSuccess: (data: SandboxResponse) => {
      // Invalidate and refetch sandboxes list
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      // Pre-populate the detail cache
      queryClient.setQueryData(sandboxKeys.detail(data.id), data);
      // Invalidate sandbox group sandboxes list so group detail page refreshes
      if (data.sandboxGroupId) {
        queryClient.invalidateQueries({
          queryKey: sandboxGroupKeys.sandboxes(data.sandboxGroupId),
        });
      }
      toast.success(`Sandbox "${data.id}" created successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create sandbox: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a sandbox
 *
 * @example
 * const { mutate, isPending } = useDeleteSandbox();
 * mutate('sandbox-123');
 */
export function useDeleteSandbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sandboxGroupId }: { id: string; sandboxGroupId?: string }) =>
      sandboxService.delete(id, sandboxGroupId),
    onSuccess: (_, { id, sandboxGroupId }) => {
      // Invalidate the list cache
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: sandboxKeys.detail(id) });
      // Invalidate sandbox group sandboxes list so group detail page refreshes
      if (sandboxGroupId) {
        queryClient.invalidateQueries({
          queryKey: sandboxGroupKeys.sandboxes(sandboxGroupId),
        });
      }
      toast.success(`Sandbox "${id}" deleted successfully`);
    },
    onError: (error: Error, { id }) => {
      showErrorToast(`Failed to delete sandbox "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to resume a stopped sandbox
 *
 * @example
 * const { mutate, isPending } = useResumeSandbox();
 * mutate('sandbox-123');
 */
export function useResumeSandbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sandboxGroupId }: { id: string; sandboxGroupId?: string }) =>
      sandboxService.resume(id, sandboxGroupId),
    onSuccess: (_, { id, sandboxGroupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      if (sandboxGroupId) {
        queryClient.invalidateQueries({
          queryKey: sandboxGroupKeys.sandboxes(sandboxGroupId),
        });
      }
      toast.success(`Sandbox "${id}" resumed successfully`);
    },
    onError: (error: Error, { id }) => {
      showErrorToast(`Failed to resume sandbox "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to stop a sandbox
 *
 * @example
 * const { mutate, isPending } = useStopSandbox();
 * mutate('sandbox-123');
 */
export function useStopSandbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, sandboxGroupId }: { id: string; sandboxGroupId?: string }) =>
      sandboxService.stop(id, sandboxGroupId),
    onSuccess: (_, { id, sandboxGroupId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      if (sandboxGroupId) {
        queryClient.invalidateQueries({
          queryKey: sandboxGroupKeys.sandboxes(sandboxGroupId),
        });
      }
      toast.success(`Sandbox "${id}" stopped successfully`);
    },
    onError: (error: Error, { id }) => {
      showErrorToast(`Failed to stop sandbox "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to create a snapshot of a sandbox
 *
 * @example
 * const { mutate, isPending } = useCreateSnapshot();
 * mutate({ id: 'sandbox-123', labels: {} });
 */
export function useCreateSnapshot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      labels,
      sandboxGroupId,
    }: {
      id: string;
      labels?: SnapshotRequest['labels'];
      sandboxGroupId?: string;
    }) => sandboxService.createSnapshot(id, { labels }, sandboxGroupId),
    onSuccess: (data, { id, sandboxGroupId }) => {
      queryClient.invalidateQueries({ queryKey: ['snapshots'] });
      if (sandboxGroupId) {
        queryClient.invalidateQueries({
          queryKey: sandboxGroupKeys.snapshots(sandboxGroupId),
        });
      }
      toast.success(`Snapshot "${data.snapshotId}" created successfully from sandbox "${id}"`);
    },
    onError: (error: Error, { id }) => {
      showErrorToast(`Failed to create snapshot for sandbox "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to commit a sandbox to a new disk image
 *
 * @example
 * const { mutate, isPending } = useCommitSandbox();
 * mutate({ id: 'sandbox-123', labels: {} });
 */
export function useCommitSandbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      labels,
      sandboxGroupId,
    }: {
      id: string;
      labels?: CommitSandboxRequest['labels'];
      sandboxGroupId?: string;
    }) => sandboxService.commitSandbox(id, { labels }, sandboxGroupId),
    onSuccess: (data, { id, sandboxGroupId }) => {
      queryClient.invalidateQueries({ queryKey: ['diskImages'] });
      if (sandboxGroupId) {
        queryClient.invalidateQueries({
          queryKey: sandboxGroupKeys.diskImages(sandboxGroupId),
        });
      }
      toast.success(`Disk image "${data.diskImage.id}" created successfully from sandbox "${id}"`);
    },
    onError: (error: Error, { id }) => {
      showErrorToast(`Failed to commit sandbox "${id}": ${error.message}`);
    },
  });
}

/**
 * Hook to execute a command in a sandbox
 *
 * @example
 * const { mutateAsync, isPending } = useExecuteCommand();
 * const result = await mutateAsync({ sandboxId: 'sandbox-123', command: 'ls -la' });
 */
export function useExecuteCommand() {
  return useMutation({
    mutationFn: ({
      sandboxId,
      command,
      workingDirectory,
      containerName,
      sandboxGroupId,
    }: {
      sandboxId: string;
      command: string;
      workingDirectory?: string;
      containerName?: string;
      sandboxGroupId?: string;
    }) =>
      sandboxService.executeCommand(
        sandboxId,
        command,
        workingDirectory,
        containerName,
        sandboxGroupId
      ),
    onError: (error: Error) => {
      showErrorToast(`Command execution failed: ${error.message}`);
    },
  });
}

/**
 * Hook to create a batch of sandboxes
 *
 * @example
 * const { mutate, isPending } = useCreateBatchSandbox();
 * mutate(batchRequest, { onSuccess: (data) => console.log(data) });
 */
export function useCreateBatchSandbox() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: import('@/types').BatchSandboxRequest) =>
      sandboxService.createBatch(request),
    onSuccess: (data: import('@/types').BatchSandboxResponse) => {
      // Invalidate and refetch sandboxes list
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...sandboxKeys.all, 'count'] });

      const successMsg = `Batch creation completed: ${data.succeeded} succeeded, ${data.failed} failed`;
      if (data.failed > 0) {
        showErrorToast(successMsg, { duration: 5000 });
      } else {
        toast.success(successMsg);
      }
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to create batch: ${error.message}`);
    },
  });
}

/**
 * Hook to add a port to a sandbox
 *
 * @example
 * const { mutate, isPending } = useAddPort();
 * mutate({ sandboxId: 'sandbox-123', port: 8080, name: 'http' });
 */
export function useAddPort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      sandboxGroupId,
      ...request
    }: { sandboxId: string; sandboxGroupId?: string } & AddPortRequest) =>
      sandboxService.addPort(sandboxId, request, sandboxGroupId),
    onSuccess: (data, { sandboxId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      toast.success(`Port ${data.port} added successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to add port: ${error.message}`);
    },
  });
}

/**
 * Hook to remove a port from a sandbox
 *
 * @example
 * const { mutate, isPending } = useRemovePort();
 * mutate({ sandboxId: 'sandbox-123', port: 8080 });
 */
export function useRemovePort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      sandboxGroupId,
      ...request
    }: { sandboxId: string; sandboxGroupId?: string } & RemovePortRequest) =>
      sandboxService.removePort(sandboxId, request, sandboxGroupId),
    onSuccess: (_, { sandboxId, port, name }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      const portIdentifier = name || port;
      toast.success(`Port ${portIdentifier} removed successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to remove port: ${error.message}`);
    },
  });
}

/**
 * Hook to patch (partially update) a single port on a sandbox
 *
 * @example
 * const { mutate, isPending } = usePatchPort();
 * mutate({ sandboxId: 'sandbox-123', port: 8080, auth: { anonymous: true } });
 */
export function usePatchPort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      sandboxGroupId,
      ...request
    }: { sandboxId: string; sandboxGroupId?: string } & PatchPortRequest) =>
      sandboxService.patchPort(sandboxId, request, sandboxGroupId),
    onSuccess: (_, { sandboxId, port, name }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      const portIdentifier = name || port;
      toast.success(`Port ${portIdentifier} updated successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update port: ${error.message}`);
    },
  });
}

/**
 * Hook to update all ports on a sandbox
 *
 * @example
 * const { mutate, isPending } = useUpdatePorts();
 * mutate({ sandboxId: 'sandbox-123', ports: [{ port: 8080 }, { port: 3000, name: 'api' }] });
 */
export function useUpdatePorts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      sandboxGroupId,
      ...request
    }: { sandboxId: string; sandboxGroupId?: string } & UpdatePortsRequest) =>
      sandboxService.updatePorts(sandboxId, request, sandboxGroupId),
    onSuccess: (data, { sandboxId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      toast.success(`Ports updated successfully (${data.length} ports)`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to update ports: ${error.message}`);
    },
  });
}

/**
 * Hook to fetch sandbox stats with periodic polling.
 * Useful for displaying token usage and other metrics.
 *
 * @param id - The sandbox ID
 * @param enabled - Whether to enable the query (defaults to true). Set to false when sandbox is not running.
 * @param refetchIntervalMs - Polling interval in milliseconds (defaults to 5000ms)
 * @example
 * const { data, isLoading, error } = useSandboxStats('sandbox-123', isRunning);
 */
export function useSandboxStats(
  id: string,
  enabled: boolean = true,
  refetchIntervalMs: number = 5000,
  sandboxGroupId?: string
) {
  return useQuery({
    queryKey: [...sandboxKeys.detail(id), 'stats'],
    queryFn: () => sandboxService.getStats(id, sandboxGroupId),
    enabled: !!id && enabled,
    staleTime: refetchIntervalMs / 2, // Consider stale after half the polling interval
    refetchInterval: enabled ? refetchIntervalMs : false,
  });
}

/**
 * Hook to add a volume mount to a running sandbox
 *
 * @example
 * const { mutate, isPending } = useAddVolumeMount();
 * mutate({ sandboxId: 'sandbox-123', volumeMount: { volumeName: 'my-volume', mountpoint: '/mnt/data' } });
 */
export function useAddVolumeMount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      volumeMount,
      containerName,
      sandboxGroupId,
    }: {
      sandboxId: string;
      volumeMount: SandboxVolumeMount;
      containerName?: string;
      sandboxGroupId?: string;
    }) => sandboxService.addVolumeMount(sandboxId, volumeMount, containerName, sandboxGroupId),
    onSuccess: (_, { sandboxId, volumeMount }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      toast.success(`Volume "${volumeMount.volumeName}" added successfully`);
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to add volume: ${error.message}`);
    },
  });
}

/**
 * Hook to add pod-level volumes and/or mount volumes into pod containers (batch API).
 *
 * @example
 * const { mutate, isPending } = useAddPodVolumeMounts();
 * mutate({ sandboxId: 'sandbox-123', request: { volumes: [...], containerMounts: [...] } });
 */
export function useAddPodVolumeMounts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sandboxId,
      request,
      sandboxGroupId,
    }: {
      sandboxId: string;
      request: AddPodVolumeMountsRequest;
      sandboxGroupId?: string;
    }) => sandboxService.addPodVolumeMounts(sandboxId, request, sandboxGroupId),
    onSuccess: (_, { sandboxId }) => {
      queryClient.invalidateQueries({ queryKey: sandboxKeys.detail(sandboxId) });
      queryClient.invalidateQueries({ queryKey: sandboxKeys.lists() });
      toast.success('Pod volumes added successfully');
    },
    onError: (error: Error) => {
      showErrorToast(`Failed to add pod volumes: ${error.message}`);
    },
  });
}
